import type { FilterConfigItem } from "@shared/components/DynamicFilter/index.vue";
import type { PaginatedQuery } from "@shared/types/list";
import { DEFAULT_PAGE_SIZE } from "@shared/types/list";
import { resolveCombinedSearchForRequest, resolveMultiSelectForRequest } from "@shared/utils/filterQuery";
import { t } from "@shared/utils/i18n";
import { AMAZON_VC_WAREHOUSE_COMBINED_SEARCH_OPTIONS, AMAZON_VC_WAREHOUSE_DATE_OPTIONS, AMAZON_VC_WAREHOUSE_KIND_OPTIONS, type AmazonVcWarehouseKind } from "./data";

export interface AmazonVcWarehouseQueryParam extends PaginatedQuery {
    warehouseKind: AmazonVcWarehouseKind;
    storeId: string[];
    region: (string | number)[];
    country: (string | number)[];
    zone: (string | number)[];
    /** 创建时间范围 [start, end] */
    create_date?: string[];
    /** 仓库代码：UiCombinedSelect 模糊 string / 精确 string[] */
    whCode: string;
    /** 仓库名称：仅模糊搜索 */
    whName: string;
}

export const DEFAULT_AMAZON_VC_WAREHOUSE_QUERY: AmazonVcWarehouseQueryParam = {
    warehouseKind: "PO",
    storeId: [],
    region: [],
    country: [],
    zone: [],
    whCode: "",
    whName: "",
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
};

/** POST /manage/warehouseAmazonVc/list 请求体 */
export function toAmazonVcWarehouseListRequestParams(query: AmazonVcWarehouseQueryParam): Record<string, unknown> {
    const body: Record<string, unknown> = {
        page: query.page,
        perPage: query.limit,
        vcType: query.warehouseKind || undefined,
        storeId: resolveMultiSelectForRequest(query.storeId),
        regionCode: resolveMultiSelectForRequest(query.region),
        countryCode: resolveMultiSelectForRequest(query.country),
        zoneCode: resolveMultiSelectForRequest(query.zone),
    };
    if (query.create_date?.length === 2) {
        if (query.create_date[0]) body.createTimeFrom = query.create_date[0];
        if (query.create_date[1]) body.createTimeTo = query.create_date[1];
    }
    const warehouseCode = resolveCombinedSearchForRequest(query.whCode as unknown as string | string[]);
    if (warehouseCode !== undefined) body.warehouseCode = warehouseCode;
    const warehouseName = resolveCombinedSearchForRequest(query.whName as unknown as string | string[]);
    if (warehouseName !== undefined) body.warehouseName = warehouseName;

    return body;
}

/** POST /manage/warehouseAmazonVc/export 请求体 */
export function toAmazonVcWarehouseExportRequestParams(query: AmazonVcWarehouseQueryParam): Record<string, unknown> {
    const params = toAmazonVcWarehouseListRequestParams(query);
    delete params.page;
    delete params.perPage;

    return params;
}

export function buildAmazonVcWarehouseFilterConfig(): FilterConfigItem[] {
    return [
        {
            type: "RadioButton",
            key: "warehouseKind",
            label: "",
            options: AMAZON_VC_WAREHOUSE_KIND_OPTIONS,
        },
        { type: "UiMultipleSelect", key: "storeId", label: t("店铺"), placeholder: t("店铺"), options: "storeOptions", width: 62 },
        { type: "UiMultipleSelect", key: "region", label: t("区域"), placeholder: t("区域"), options: "regionOptions", width: 62 },
        { type: "UiMultipleSelect", key: "country", label: t("国家"), placeholder: t("国家"), options: "countryOptions", width: 62 },
        { type: "UiMultipleSelect", key: "zone", label: t("分区"), placeholder: t("分区"), options: "zoneOptions", width: 62 },
        {
            type: "TimeTypeSelect",
            key: "create_date",
            label: t("日期"),
            options: AMAZON_VC_WAREHOUSE_DATE_OPTIONS,
            props: { showShortcuts: true },
        },
        {
            type: "UiCombinedSelect",
            key: "amazon_vc_warehouse_search",
            label: t("搜索"),
            options: AMAZON_VC_WAREHOUSE_COMBINED_SEARCH_OPTIONS,
            props: { selectValue: "whCode", placeholder: t("搜索内容") },
        },
    ];
}
